import { useEffect, useState } from "react";
import Button from "./ui/Button.jsx";
import {
  mapAuth0ConnectApiError,
  validateAuth0ConnectFields,
} from "./onboarding/auth0ConnectValidation.js";
import { AUTH0_M2M_GUIDANCE } from "./onboarding/auth0ConnectGuidance.js";
import Auth0ScopeStatusList from "./onboarding/Auth0ScopeStatusList.jsx";
import { getProjectTenantDisplayUrl } from "../utils/projectProvider.js";

/**
 * Credential-update surface for existing Auth0 projects (AUTH0.IMPL.3-rev).
 * Primary project creation uses ConnectAuth0Panel in NewProjectModal.
 */

/**
 * @param {{ open: boolean, onClose: () => void, project: { projectId?: string, org?: { url?: string } } | null, onConnected?: () => void }} props
 */
export default function ConnectAuth0Modal({ open, onClose, project, onConnected }) {
  const projectId = project?.projectId;
  const [tenantDomain, setTenantDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState(null);
  const [testOk, setTestOk] = useState(false);
  const [scopesGranted, setScopesGranted] = useState(/** @type {string[]} */ ([]));
  const [scopeStatus, setScopeStatus] = useState(
    /** @type {{ scope: string, granted: boolean, tier: string }[]} */ ([]),
  );
  const [scopeWarning, setScopeWarning] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTenantDomain(getProjectTenantDisplayUrl(project) || "");
    setClientId("");
    setClientSecret("");
    setError(null);
    setTestOk(false);
    setScopesGranted([]);
    setScopeStatus([]);
    setScopeWarning("");
  }, [open, project]);

  if (!open) return null;

  const bodyPayload = () => ({
    projectId,
    tenant_domain: tenantDomain.trim(),
    client_id: clientId.trim(),
    client_secret: clientSecret.trim(),
  });

  const handleTest = async () => {
    setError(null);
    setTestOk(false);
    setScopesGranted([]);
    setScopeStatus([]);
    setScopeWarning("");
    const validated = validateAuth0ConnectFields({
      tenant_domain: tenantDomain,
      client_id: clientId,
      client_secret: clientSecret,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    if (!projectId) {
      setError("Missing project.");
      return;
    }
    setTestBusy(true);
    try {
      const res = await fetch("/api/idp/auth0/test-connection", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        const missing = Array.isArray(data.scopes_missing_required)
          ? data.scopes_missing_required
          : Array.isArray(data.scopes_missing)
            ? data.scopes_missing
            : [];
        if (missing.length > 0) {
          setError(
            `${data.error || "Required scopes missing"}: ${missing.join(", ")}`,
          );
        } else {
          setError(mapAuth0ConnectApiError(data));
        }
        return;
      }
      setTestOk(true);
      setScopesGranted(Array.isArray(data.scopes_granted) ? data.scopes_granted : []);
      setScopeStatus(Array.isArray(data.scope_status) ? data.scope_status : []);
      setScopeWarning(typeof data.scope_warning === "string" ? data.scope_warning : "");
    } catch (e) {
      setError(e?.message || "Test connection failed");
    } finally {
      setTestBusy(false);
    }
  };

  const handleConnect = async () => {
    if (!testOk || !projectId) return;
    setError(null);
    setConnectBusy(true);
    try {
      const res = await fetch("/api/idp/auth0/connect", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(mapAuth0ConnectApiError(data));
      }
      onConnected?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || "Connect failed");
    } finally {
      setConnectBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(5, 8, 16, 0.72)",
        display: "grid",
        placeItems: "center",
        padding: "24px",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-auth0-title"
    >
      <div
        style={{
          width: "100%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflow: "auto",
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "10px",
          padding: "24px",
        }}
      >
        <h2 id="connect-auth0-title" style={{ fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>
          Connect Auth0 tenant
        </h2>
        <p className="text-secondary" style={{ fontSize: "12px", marginBottom: "16px", whiteSpace: "pre-line" }}>
          {AUTH0_M2M_GUIDANCE}
        </p>

        <div style={{ display: "grid", gap: "12px", marginBottom: "16px" }}>
          <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <span className="text-secondary">Tenant domain</span>
            <input
              type="text"
              value={tenantDomain}
              onChange={(e) => {
                setTenantDomain(e.target.value);
                setTestOk(false);
              }}
              placeholder="your-tenant.us.auth0.com"
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid var(--border)",
                color: "var(--text-primary)",
                background: "var(--bg-input)",
              }}
            />
          </label>
          <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <span className="text-secondary">M2M Client ID</span>
            <input
              type="text"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setTestOk(false);
              }}
              autoComplete="off"
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid var(--border)",
                color: "var(--text-primary)",
                background: "var(--bg-input)",
              }}
            />
          </label>
          <label style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <span className="text-secondary">M2M Client Secret</span>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type={showSecret ? "text" : "password"}
                value={clientSecret}
                onChange={(e) => {
                  setClientSecret(e.target.value);
                  setTestOk(false);
                }}
                autoComplete="off"
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid var(--border)",
                  color: "var(--text-primary)",
                  background: "var(--bg-input)",
                }}
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                style={{ fontSize: "12px", padding: "0 10px" }}
              >
                {showSecret ? "Hide" : "Show"}
              </button>
            </div>
          </label>
        </div>

        {testOk ? (
          <>
            <p style={{ fontSize: "13px", color: "var(--status-ok, #4ade80)", marginBottom: "8px" }}>
              ✓ Connection verified — {scopesGranted.length} scope{scopesGranted.length === 1 ? "" : "s"} granted
            </p>
            <Auth0ScopeStatusList granted={scopesGranted} scopeStatus={scopeStatus} compact />
            {scopeWarning ? (
              <p style={{ fontSize: "12px", color: "var(--warning)", marginTop: "8px" }}>{scopeWarning}</p>
            ) : null}
          </>
        ) : null}

        {error ? (
          <p style={{ fontSize: "13px", color: "var(--status-error, #f87171)", marginBottom: "12px" }}>{error}</p>
        ) : null}

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="secondary" disabled={testBusy} onClick={() => void handleTest()}>
            {testBusy ? "Testing…" : "Test Connection"}
          </Button>
          <Button type="button" disabled={!testOk || connectBusy} onClick={() => void handleConnect()}>
            {connectBusy ? "Connecting…" : "Connect"}
          </Button>
        </div>
      </div>
    </div>
  );
}
